/*
 * @Description: 基础工具函数
 * @Author: erickuang
 * @Date: 2021-03-04 11:46:40
 * @LastEditor: erickuang
 * @LastEditTime: 2022-10-28 10:52:37
 */

import { getObjectValueByKeyReference } from './main';

/**
 * 判断值是否为Nullish
 *
 * @export
 * @param = function {any} val 原始值
 * @return {boolean} 是否为Nullish
 */
export const isNullish = function (val: any) {
  return val === null || val === undefined;
};

/**
 * 判断输入是否是合法的对象
 *
 * @param {*} value 输入值
 * @return {boolean} 输入值是否为对象
 */
export const isValueObject = (value: any) => value?.toString() === '[object Object]' || false;

/**
 * 判断对象类型
 *
 * @param {*} obj 对象
 * @return {string} Array Object Function
 */
export const getObjectType = (obj: unknown) => Object.prototype.toString.call(obj).slice(8, -1);

/**
 * 判断对象是否为空对象
 *
 * @param {Record<string, any>} value
 * @return {boolean} 是否为空对象
 */
export const isValueVoidObject = (value: Record<string, any>) => {
  if (!isValueObject(value)) return true;
  return Object.keys(value).length === 0;
};

/**
 * 获得数字的整数部分
 *
 * @param {number} num 数字
 * @return {*}  {number} 数字的整数部分
 */
export const getIntegerOfNumber = (num: number): number => {
  if (isNaN(Number(num))) return NaN;
  return ~~num;
};

/**
 * 随机获取数组中的一个元素
 *
 * @template T
 * @param {T[]} arr 数组
 * @return {*}  {(T | undefined)} 随机获取的元素
 */
export const getRandomItemOfArray = <T>(arr: T[]): T | undefined => {
  if (!Array.isArray(arr)) return;
  let randomNum = Math.floor(Math.random() * arr.length);
  if (randomNum === arr.length) randomNum = randomNum - 1;
  return arr[randomNum];
};

/**
 * 比较对象值是否相等
 *
 * @param {*} [ori={}] 原始对象
 * @param {*} [ref={}] 待比较对象
 * @param {*} [{ deepEqual = false }={}] 是否深度比较
 * @return {*}  {boolean} 比较结果
 */
export const isObjectsValueEqual = (ori: any, ref: any, { deepEqual = false } = {}): boolean => {
  if (!(ori instanceof Object) || !(ref instanceof Object)) return ori === ref;
  return Object.keys(ori).every((k) => {
    const oriValue = ori[k];
    const refValue = ref[k];
    if (oriValue instanceof Object && refValue instanceof Object) {
      // 比较对象类型
      if (deepEqual) {
        // 执行深度比较
        return isObjectsValueEqual(oriValue, refValue, { deepEqual });
      }
      // 对比JSON字符串
      return JSON.stringify(oriValue) === JSON.stringify(refValue);
    }
    return oriValue === refValue;
  });
};

/**
 * 将对象键值组合为字符串
 *
 * @param {*} [obj={}] 对象
 * @param {string} [joinerKeyAndValue=''] 链接键值的字符
 * @param {string} [joinerChunk=''] 链接键值chunk的字符
 * @param {string} [prefixMark=''] 前缀符号
 * @return {*} 结果
 */
export const concatStringWithObject = (
  obj = {},
  joinerKeyAndValue = '',
  joinerChunk = '',
  prefixMark = '',
) => {
  if (!isValueObject(obj)) return;
  const result = prefixMark;
  const appendString = Object.entries(obj)
    .map(([key, value]) => `${key}${joinerKeyAndValue}${value}`)
    .join(joinerChunk);
  return `${result}${appendString}`;
};

/**
 * 获得数组中排除了指定key为指定值的剩余部分
 *
 * @template T 对象数组类型
 * @template K
 * @param {K} refKey 指定key，可以为嵌套字符，如："test.a"
 * @param {any[]} [refValues=[]] 指定值
 * @param {T[]} originArr 原始数组
 * @return {*}  {T[]} 经过滤后的数组
 */
export const getItemsExcludedInArrByKey = <T, K extends keyof T>(
  refKey: K,
  refValues: any[] = [],
  originArr: T[],
): T[] => {
  if (!refKey || !refValues || !originArr || originArr?.length === 0 || refValues?.length === 0) {
    return originArr;
  }
  return originArr.filter((v: any) => !refValues.includes(getObjectValueByKeyReference(v, refKey as string)));
};

/**
 * 判断输入是否是整数
 *
 * @param {(number | string)} input 输入
 * @return {*}  {boolean} 是否是整数
 */
export const isInteger = (input: number | string): boolean => {
  if (isNaN(Number(input)) || isNullish(input)) return false;
  const validNumber = Number(input);
  const integerPart = getIntegerOfNumber(validNumber);
  return integerPart === validNumber;
};

export class SequenceExecutor {
  private tasks: Function[] = [];
  private currentTaskIndex = -1;
  public addTask(taskFunction: () => void) {
    this.tasks.push(taskFunction);
  }
  public getCurrentTaskIndex() {
    return this.currentTaskIndex;
  }
  public async executeTasks({ fromBegin = false, after = () => {} } = {}) {
    if (!fromBegin && this.currentTaskIndex >= this.tasks.length - 1) {
      console.log('[SequenceExecutor] Task already finished.');
    }
    const validIndex = fromBegin ? 0 : this.currentTaskIndex + 1;
    for (let i = validIndex; i < this.tasks.length; i++) {
      try {
        await this.tasks[i]?.();
      } catch (error) {
        console.log(error);
      } finally {
        if (!fromBegin) this.currentTaskIndex = i;
      }
    }
    typeof after === 'function' && after();
  }
}

/**
 * 获得输入字符串的哈希码
 *
 * @param {string} str 输入字符串
 * @return {*}  {number} 哈希码
 */
export const getHashCode = (str: string): number => {
  let hash = 0;
  let i = 0;
  let chr = 0;
  if (typeof str !== 'string' || str?.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
};

/**
 * 全局计数器
 *
 * @export
 * @class GlobalCounter
 */
export class GlobalCounter {
  static currentIndex = 0;
  static distributeGlobalIndex() {
    const distribution = this.currentIndex;
    this.currentIndex += 1;
    return distribution;
  }
}

/**
 * 局部计数器
 *
 * @export
 * @class Counter
 */
export class Counter {
  currentIndex: number;
  constructor(initialIndex = 0) {
    this.currentIndex = initialIndex;
  }
  distributeIndex() {
    const distribution = this.currentIndex;
    this.currentIndex += 1;
    return distribution;
  }
  reset() {
    this.currentIndex = 0;
  }
}

/**
 * 将对象数组按照对象特征进行分类
 *
 * @template T 对象类型
 * @param {T[]} inputObjects 输入对象数组
 * @param {(obj: Record<string, any>) => string} featureMapHandler 特征映射方法
 * @return {*}  {Record<string, T[]>} 分类后的对象数组表
 */
export const splitObjectsByFeature = <T>(
  inputObjects: T[],
  featureMapHandler: (obj: any) => string,
): Record<string, T[]> => {
  if (!inputObjects || inputObjects?.length === 0 || typeof featureMapHandler !== 'function') {
    return {};
  }
  const result: Record<string, T[]> = {};
  inputObjects?.forEach?.((obj) => {
    const featureOfObj = featureMapHandler?.(obj);
    if (!result[featureOfObj]) {
      result[featureOfObj] = [obj];
    } else {
      result[featureOfObj].push(obj);
    }
  });
  return result;
};

/**
 * 获得当前运行时的 Env 对象
 *
 * @return {*} process.env 或 import.meta.env
 */
export const getCurrentEnvObject = (): any => {
  let result = {};
  try {
    result = process.env;
  } catch (error) {
    try {
      result = (import.meta as any).env;
    } catch (error) {
      result = {};
    }
  }
  return result;
};
